import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { StaffDto } from '@genoma/shared';

import { Centered } from '../../components/Centered.tsx';
import { useI18n } from '../../i18n/index.tsx';

/**
 * A list of people with accounts, and the way to add and invite one.
 *
 * Coaches and the client's P&O are managed identically — created without
 * credentials, invited from the list, corrected and re-invited — so the screen
 * is one component taking its verbs as props. The difference between the two
 * is what their role can see, which is settled in `roles.ts` and nowhere near
 * a component.
 *
 * Written once rather than twice because the second copy is where the invite
 * confirmation, or the "not invited" state, quietly stops matching the first.
 */

/** Initials, not photographs: nothing here can store an uploaded file yet. */
export const Initials = ({ name }: { name: string }) => {
  const letters = name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();

  // Stable per person: the same name always draws the same colour, so a face
  // in the list stays recognisable between visits.
  const hue = [...name].reduce((total, char) => total + char.charCodeAt(0), 0) % 360;

  return (
    <span
      aria-hidden
      className="flex size-9 shrink-0 items-center justify-center rounded-full font-mono text-[12px] font-bold text-white"
      style={{ backgroundColor: `oklch(0.58 0.09 ${hue})` }}
    >
      {letters}
    </span>
  );
};

type Details = { fullName: string; email: string; phone: string };

const EMPTY: Details = { fullName: '', email: '', phone: '' };

/**
 * The three fields, wherever they are being filled in.
 *
 * Creating an account and correcting one ask for exactly the same things, and
 * the second copy of this is where the phone field quietly stops being
 * optional in one of them, or the address stops being an `email` input.
 */
const AccountFields = ({
  form,
  setForm,
  submitLabel,
  help,
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  form: Details;
  setForm: (next: Details) => void;
  submitLabel: string;
  help: string;
  busy: boolean;
  error: string | null;
  onSubmit: () => void;
  onCancel: () => void;
}) => {
  const { t } = useI18n();
  const complete = form.fullName.trim().length > 1 && form.email.trim().length > 0;

  const field = (key: keyof Details, type = 'text') => (
    <label className="block min-w-[180px] flex-1">
      <span className="block font-mono text-[10px] tracking-[0.12em] text-faint uppercase">
        {t(`coaches.field.${key}`)}
      </span>
      <input
        type={type}
        value={form[key]}
        autoComplete="off"
        autoFocus={key === 'fullName'}
        onChange={(event) => setForm({ ...form, [key]: event.target.value })}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && complete) onSubmit();
          if (event.key === 'Escape') onCancel();
        }}
        className="mt-1 w-full rounded-xl border border-line bg-sand px-3 py-2 text-[14px] outline-none focus:border-teal"
      />
    </label>
  );

  return (
    <>
      {/* One row on a wide screen, wrapping on a narrow one, with the buttons
          on the fields' baseline rather than under them. */}
      <div className="flex flex-wrap items-end gap-3">
        {field('fullName')}
        {field('email', 'email')}
        {field('phone', 'tel')}
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            disabled={busy || !complete}
            onClick={onSubmit}
            className="cursor-pointer rounded-xl bg-teal px-4 py-2.5 text-[13.5px] font-bold text-white shadow-action transition hover:bg-teal-deep disabled:opacity-40"
          >
            {busy ? t('common.saving') : submitLabel}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="cursor-pointer border-none bg-transparent px-1 py-2 font-mono text-[11px] tracking-[0.06em] text-soft uppercase"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>

      <p className="mt-2.5 text-[12.5px] text-faint">{help}</p>
      {error && <p className="mt-2 text-[12.5px] text-[var(--color-critical)]">{error}</p>}
    </>
  );
};

const NewAccount = ({
  open,
  onClose,
  onCreated,
  create,
  helpKey,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  create: (input: { fullName: string; email: string; phone: string | null }) => Promise<unknown>;
  helpKey: string;
}) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Details>(EMPTY);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await create({ fullName: form.fullName, email: form.email, phone: form.phone || null });
      setForm(EMPTY);
      onCreated();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <section className="mt-5 rounded-2xl border border-line bg-paper px-5 py-4 shadow-card">
      <AccountFields
        form={form}
        setForm={setForm}
        submitLabel={t('coaches.create')}
        help={t(helpKey)}
        busy={busy}
        error={error}
        onSubmit={() => void submit()}
        onCancel={onClose}
      />
    </section>
  );
};

/**
 * Correcting somebody already on the list, in place.
 *
 * In the row rather than on a page of their own: P&O have nothing else to
 * manage about them, and sending somebody to a detail screen to fix a typo in
 * an address is three navigations for one field. A coach does have such a
 * screen, because their assignments need one — so that is where a coach is
 * edited, and this list stays the place for the accounts that have no depth.
 */
const EditAccount = <T extends StaffDto>({
  person,
  update,
  onSaved,
  onCancel,
}: {
  person: T;
  update: (
    personId: string,
    changes: { fullName: string; email: string; phone: string | null },
  ) => Promise<unknown>;
  onSaved: () => void;
  onCancel: () => void;
}) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Details>({
    fullName: person.fullName,
    email: person.email,
    phone: person.phone ?? '',
  });

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await update(person.personId, {
        fullName: form.fullName,
        email: form.email,
        phone: form.phone || null,
      });
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full">
      <AccountFields
        form={form}
        setForm={setForm}
        submitLabel={t('common.save')}
        // What changing the address actually does, said where it is being
        // changed: it moves the invitation *and* the way in.
        help={t(person.canSignIn ? 'coaches.editHelpSignedIn' : 'coaches.editHelp')}
        busy={busy}
        error={error}
        onSubmit={() => void submit()}
        onCancel={onCancel}
      />
    </div>
  );
};

/**
 * The button that hands somebody their way in.
 *
 * Creating an account and inviting somebody are separate acts, so this reads
 * the address at the moment it is pressed — which is what makes a mistyped
 * address a correction rather than a password sent to a stranger.
 *
 * A second press mints a new password, because the old one is a hash and
 * cannot be read back. For somebody who has already signed in that is a reset,
 * so the button asks first and says which it is.
 */
const Invite = ({
  person,
  invite,
  onSent,
}: {
  person: StaffDto;
  invite: (personId: string) => Promise<unknown>;
  onSent: () => void;
}) => {
  const { t, locale } = useI18n();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      await invite(person.personId);
      setConfirming(false);
      onSent();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const resend = person.invitedAt !== null;

  return (
    <span className="relative z-10 flex shrink-0 items-center gap-2">
      {person.invitedAt ? (
        <span className="font-mono text-[9.5px] tracking-[0.08em] text-faint uppercase">
          {person.lastLoginAt
            ? t('coaches.hasEntered')
            : t('coaches.invitedOn', {
                date: new Date(person.invitedAt).toLocaleDateString(locale),
              })}
        </span>
      ) : (
        <span className="rounded-full border border-[#E3C88A] bg-[#FDF6E6] px-2 py-0.5 font-mono text-[9.5px] tracking-[0.08em] text-[#8A6A1F] uppercase">
          {t('coaches.notInvited')}
        </span>
      )}

      <button
        type="button"
        disabled={busy}
        onClick={() => (confirming || !resend ? void send() : setConfirming(true))}
        title={error ?? undefined}
        className={`cursor-pointer rounded-xl px-3 py-1.5 text-[12.5px] font-semibold transition disabled:opacity-40 ${
          confirming
            ? 'bg-[var(--color-critical)] text-white'
            : resend
              ? 'border border-line bg-paper text-soft hover:border-teal hover:text-teal'
              : 'bg-teal text-white shadow-action hover:bg-teal-deep'
        }`}
      >
        {busy
          ? t('common.saving')
          : confirming
            ? t('coaches.resendConfirm')
            : resend
              ? t('coaches.resend')
              : t('coaches.sendInvitation')}
      </button>
    </span>
  );
};

/** One person, at rest: who they are, and the two things you can do to them. */
const Row = <T extends StaffDto>({
  person,
  canManage,
  invite,
  onInvited,
  onEdit,
  extra,
  href,
}: {
  person: T;
  canManage: boolean;
  invite: (personId: string) => Promise<unknown>;
  onInvited: () => void;
  /** Absent when this list does not correct people in place. */
  onEdit?: (() => void) | undefined;
  extra?: ((person: T) => ReactNode) | undefined;
  href?: ((person: T) => string) | undefined;
}) => {
  const { t } = useI18n();

  return (
    <>
      <Initials name={person.fullName} />
      <span className="min-w-[180px] flex-1">
        {href ? (
          <a
            href={href(person)}
            className="block text-[14.5px] font-bold after:absolute after:inset-0 after:content-[''] hover:text-teal"
          >
            {person.fullName}
          </a>
        ) : (
          <span className="block text-[14.5px] font-bold">{person.fullName}</span>
        )}
        <span className="block text-[12.5px] break-all text-soft">
          {person.email}
          {person.phone && ` · ${person.phone}`}
        </span>
      </span>

      {extra?.(person)}

      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          // Above the row-wide link overlay, where a list has one.
          className="relative z-10 shrink-0 cursor-pointer rounded-xl border border-line bg-paper px-3 py-1.5 text-[12.5px] font-semibold text-soft transition hover:border-teal hover:text-teal"
        >
          {t('common.edit')}
        </button>
      )}

      {canManage ? (
        <Invite person={person} invite={invite} onSent={onInvited} />
      ) : (
        // A read-only viewer still sees whether somebody has an account they
        // have used: it explains an empty seat without a button.
        <span className="font-mono text-[9.5px] tracking-[0.08em] text-faint uppercase">
          {person.lastLoginAt
            ? t('coaches.hasEntered')
            : person.invitedAt
              ? t('coaches.invitedOn', {
                  date: new Date(person.invitedAt).toLocaleDateString('es'),
                })
              : t('coaches.notInvited')}
        </span>
      )}
    </>
  );
};

export const StaffSection = <T extends StaffDto>({
  title,
  subtitle,
  emptyKey,
  newKey,
  helpKey,
  footerKey,
  canManage,
  load: loadList,
  create,
  update,
  invite,
  extra,
  href,
}: {
  title: string;
  subtitle: (count: number) => string;
  emptyKey: string;
  /**
   * What the add button calls the thing being added.
   *
   * Every other word on this screen is generic — "Crear", "Correo", "sin
   * invitar" — but this one names it, and the shared component has no idea
   * whether it is looking at coaches or at the client's P&O. Left to the
   * component's own catalogue it said "Crear coach" on the P&O screen.
   */
  newKey: string;
  helpKey: string;
  footerKey: string;
  /** Read-only viewers get the list and none of the verbs. */
  canManage: boolean;
  load: () => Promise<T[]>;
  create: (input: { fullName: string; email: string; phone: string | null }) => Promise<unknown>;
  /**
   * Correcting somebody, when this list is where that happens.
   *
   * Absent for a list whose rows lead to a screen of their own: two places to
   * edit the same three fields is two places to keep in step.
   */
  update?: (
    personId: string,
    changes: { fullName: string; email: string; phone: string | null },
  ) => Promise<unknown>;
  invite: (personId: string) => Promise<unknown>;
  /** Anything this particular list shows beside the address. */
  extra?: (person: T) => ReactNode;
  /** Where a row leads, when it leads anywhere. */
  href?: (person: T) => string;
}) => {
  const { t } = useI18n();
  const [rows, setRows] = useState<T[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  /** At most one row is being corrected at a time. */
  const [editing, setEditing] = useState<string | null>(null);
  // The verb itself when this list corrects people, so the row below narrows
  // with it rather than asserting past a boolean.
  const editRow = canManage ? update : undefined;

  const load = useCallback(() => {
    loadList()
      .then(setRows)
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : t('errors.generic')),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(load, [load]);

  if (error) return <Centered>{error}</Centered>;
  if (!rows) return <Centered>{t('common.loading')}</Centered>;

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-[-0.025em]">{title}</h1>
          <p className="mt-0.5 text-[13.5px] text-soft">{subtitle(rows.length)}</p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setAdding((was) => !was)}
            className="cursor-pointer rounded-xl bg-teal px-3.5 py-2.5 text-[13.5px] font-bold text-white shadow-action transition hover:bg-teal-deep"
          >
            {t(newKey)}
          </button>
        )}
      </header>

      {canManage && (
        <NewAccount
          open={adding}
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            load();
          }}
          create={create}
          helpKey={helpKey}
        />
      )}

      <div className="mt-5 overflow-hidden rounded-2xl border border-line bg-paper shadow-card">
        {rows.length === 0 ? (
          <p className="px-4 py-8 text-center text-[13.5px] text-faint">{t(emptyKey)}</p>
        ) : (
          rows.map((person) => (
            <div
              key={person.personId}
              className={`relative flex flex-wrap items-center gap-3 border-b border-line-soft px-4 py-3 transition last:border-b-0 ${
                editing === person.personId ? 'bg-tint-soft' : 'hover:bg-sand'
              }`}
            >
              {editRow && editing === person.personId ? (
                <EditAccount
                  person={person}
                  update={editRow}
                  onSaved={() => {
                    setEditing(null);
                    load();
                  }}
                  onCancel={() => setEditing(null)}
                />
              ) : (
                <Row
                  person={person}
                  canManage={canManage}
                  invite={invite}
                  onInvited={load}
                  onEdit={editRow ? () => setEditing(person.personId) : undefined}
                  extra={extra}
                  href={href}
                />
              )}
            </div>
          ))
        )}
      </div>

      <p className="mt-4 text-xs text-faint">{t(footerKey)}</p>
    </div>
  );
};
